import React, {ForwardedRef, MouseEventHandler, useEffect, useMemo, useRef} from 'react';
import './styles/reviewFeed.scss';
import {useTranslation} from 'react-i18next';
import {AwesomeIcon, stopPropagation} from '@dvrd/dvr-controls';

import {DotsLoader} from '../../controls/loader/dotsLoader';

import ProductReview from '../../../models/productReviewModel';

import {elementIsInView} from '../../../util/utils';

import classNames from 'classnames';

import {Rating} from '../../../util/types';

import {BolmateButton} from '../../controls';

import Image from '../../misc/image';

import RouterLink from '../../controls/link/routerLink';

import ReviewCounts, {ReviewCountsRef} from '../reviewCounts/reviewCounts';

import {Link} from 'react-router';

import ReviewComponent from '../reviewComponent';

import {DefaultNumberObject} from '../../../util/defaultObject';

import FeedProductSelectController from './feedProductSelect/feedProductSelectController';

interface Props {
    onLoadMore: (callback: VoidFunction) => void;
    onOpenFilter: MouseEventHandler;
    onCloseFilter: MouseEventHandler;
    onClearRatingFilter: MouseEventHandler;
    reviews: Array<ProductReview>;
    loading: boolean;
    loadingMore: boolean;
    total: number;
    filterTotal: number;
    ratingCounts: DefaultNumberObject<Rating>;
    ratingsFilter: Set<Rating>;
    filterOpen: boolean;
    filterRef: ForwardedRef<ReviewCountsRef>;
}

export default function ReviewFeed(props: Props) {
    const {t} = useTranslation();
    const {
        reviews,
        loading,
        onLoadMore,
        total,
        filterOpen,
        onOpenFilter,
        onCloseFilter,
        ratingsFilter,
        ratingCounts,
        filterTotal,
        onClearRatingFilter,
        filterRef
    } = props;
    const scrolling = useRef(false);
    const loadingMore = useRef(false);
    const hasMore = useMemo(() => {
        return reviews.length < total;
    }, [reviews, total]);

    function shouldLoadMore(): boolean {
        if (!hasMore) return false;
        if (!reviews.length) return true;
        const checkIndex = Math.max(0, reviews.length - 3);
        const review = reviews[checkIndex];
        if (!review) return true;
        const element = document.getElementById(`review-${review.id}`);
        return !element || elementIsInView(element);
    }

    function scrollListener() {
        if (loadingMore.current || scrolling.current) return;
        window.requestAnimationFrame(() => {
            if (shouldLoadMore())
                onLoadMore(() => {
                    loadingMore.current = false;
                });
            else loadingMore.current = false;
            scrolling.current = false;
        });
        loadingMore.current = true;
        scrolling.current = true;
    }

    function renderReview(review: ProductReview, index: number) {
        const productPageLink = (
            <RouterLink
                to={`https://www.bol.com/nl/nl/p/${review.title?.replace('/', '-')}/${review.globalId}#product-reviews`}
                target='_blank'
                title={t('reviewAnalytics.reviewFeed.viewOnBol', 'Bekijk product op bol')}
                onClick={stopPropagation}
            >
                <Image name='bol-fav-32' alt='' className='logo-link' />
            </RouterLink>
        );
        return (
            <div key={index} className='review-block' id={`review-${review.id}`}>
                <ReviewComponent review={review} withLinks />
                <Link to={`/review-analytics/${review.ean}`} className='product-container'>
                    <label className='product-title'>{review.title}</label>
                    <div className='row'>
                        <label className='subtitle'>EAN: {review.ean}</label>
                        <div className='product-links'>
                            <RouterLink
                                to={`/review-analytics/${review.ean}`}
                                title={t(
                                    'reviewAnalytics.reviewFeed.viewAllReviews',
                                    'Alle reviews van dit product bekijken en analyseren'
                                )}
                                onClick={stopPropagation}
                            >
                                <Image name='logo-no-text' alt='' className='logo-link' />
                            </RouterLink>
                            <RouterLink
                                to={`https://partner.bol.com/sdd/product-overview/products/${review.globalId}`}
                                target='_blank'
                                title={t(
                                    'reviewAnalytics.reviewFeed.viewInSellerAccount',
                                    'Bekijk product in jouw verkoopaccount'
                                )}
                                onClick={stopPropagation}
                            >
                                <Image name='bol-16' alt='' className='logo-link' />
                            </RouterLink>
                            {productPageLink}
                        </div>
                    </div>
                    <img className='image-container' src={review.imageUrl ?? ''} alt='' />
                </Link>
            </div>
        );
    }

    function renderFilter() {
        return (
            <div className='filter-control'>
                <BolmateButton
                    onClick={onOpenFilter}
                    label={t('reviewAnalytics.reviewFeed.filterStars', 'Filter aantal sterren')}
                    ornaments={{element: <AwesomeIcon name='sliders' />}}
                    className='btn-filter'
                    secondary
                />
                {!!ratingsFilter.size && (
                    <label className='clear-filter' onClick={onClearRatingFilter}>
                        {t('reviewAnalytics.reviewFeed.clearFilter', 'Filter wissen')}
                    </label>
                )}
                <FeedProductSelectController />
                <ReviewCounts
                    loading={loading}
                    total={filterTotal}
                    counts={ratingCounts.toRecord()}
                    selectedRatings={ratingsFilter}
                    onClose={onCloseFilter}
                    className={classNames('rating-filter', filterOpen && 'open')}
                    ref={filterRef}
                />
            </div>
        );
    }

    useEffect(() => {
        document.addEventListener('scroll', scrollListener, {passive: true});

        return function () {
            document.removeEventListener('scroll', scrollListener);
        };
    }, [hasMore, reviews, loadingMore, ratingsFilter]);

    const isLoading = loading || loadingMore.current;
    return (
        <div className='reviews-feed'>
            {renderFilter()}
            {reviews.map(renderReview)}
            {isLoading && (
                <div className='review-block'>
                    <div className='review-container btn'>
                        <DotsLoader className='feed-loader' dotSize='4rem' />
                    </div>
                </div>
            )}
            {!isLoading && !reviews.length && (
                <div className='empty-container'>
                    <p className='empty-message'>
                        {t(
                            'reviewAnalytics.reviewFeed.emptyIntro',
                            'Hier zie je straks al jouw productreviews in één handig overzicht.'
                        )}
                        <br />
                        {t(
                            'reviewAnalytics.reviewFeed.emptyCollecting',
                            'Data aan het verzamelen, kom morgen nog eens terug.'
                        )}
                    </p>
                </div>
            )}
        </div>
    );
}
